import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { sleep, SubscriptionSet, AppConfig, PageService, PageComponent, Head } from '@tytapp/common';
import { PlaybackSession, PlayerComponent } from '@tytapp/media-playback';
import { LiveStreamsService } from '@tytapp/common-ui';
import { UserService } from '@tytapp/user';
import { environment } from '@tytapp/environment';
import { ApiLiveStream, ApiUser } from '@tytapp/api';

@Component({
    selector: 'tyt-watch-live',
    templateUrl: './watch-live.component.html',
    styleUrls: ['./watch-live.component.scss']
})
export class WatchLiveComponent extends PageComponent implements OnInit, AfterViewInit {

    useBantaChat = false;

    constructor(
        pageService: PageService,
        private route: ActivatedRoute,
        private liveStreamsService: LiveStreamsService,
        private userService: UserService,
        private appConfig: AppConfig,
        private head: Head
    ) {
        super(pageService);

        this.playerReady = new Promise((resolve) => {
            this.resolvePlayerReady = resolve;
        });

    }

    stream: ApiLiveStream;
    timezoneString = null;
    platformChat: any = null;
    session: PlaybackSession = null;
    paywallVisible: boolean = false;
    showPlaybackOptions: boolean = false;
    showComments: boolean = true;
    showChat: boolean = true;

    technicalDifficultiesStartedAt: Date = null;
    audienceType: string = 'visitor';

    get technicalDifficulties() {
        if (!this.appConfig || !this.appConfig.appStatus || !this.appConfig.appStatus.features)
            return false;
        let enabled = this.appConfig.appStatus.features.includes('apps.web.live_technical_difficulties');
        if (enabled && !this.technicalDifficultiesStartedAt) {
            this.technicalDifficultiesStartedAt = new Date();
            let startedAt = this.technicalDifficultiesStartedAt.getTime() / 1000;
            let interval = 15 + Math.floor(Math.random() * 30);

            if (typeof window === 'undefined') {
                this.technicalDifficultiesTimeBeforeTryAgain = 0;
            } else {
                setInterval(() => {
                    let currentTime = Date.now() / 1000;

                    this.technicalDifficultiesTimeBeforeTryAgain = Math.round(Math.max(0, startedAt + interval - currentTime));
                }, 1000);
            }
        }

        return enabled;
    }

    technicalDifficultiesTimeBeforeTryAgain: number;
    showDonatePromos: boolean = true;

    get topicIdentifier() {
        if (!this.stream)
            return null;

        let date = new Date(this.stream.started);
        let dateStr = date.toISOString().substring(0, 10);
        let topicString = `tytlive_${dateStr}`;

        return topicString;
    }

    get topicTitle() {
        if (!this.stream)
            return 'TYT Live';

        let date = new Date(this.stream.started);
        let dateStr = date.toISOString().substring(0, 10);
        return `TYT Live ${dateStr}`;
    }

    showDarkThemeNotes = false;
    showChatNotes: boolean = false;

    get playbackServiceId(): string {
        if (this.session && !this.session.service) {
            console.error(`While determining playback service ID, discovered that session does not have a service:`);
            console.dir(this.session);
        }

        if (!this.session || !this.session.service.id)
            return null;

        return this.session.service.id;
    }

    @ViewChild('player')
    public player: PlayerComponent;

    playerReady: Promise<void>;
    resolvePlayerReady: Function;

    ngAfterViewInit() {
        this.resolvePlayerReady();
    }

    popOut() {
        if (!this.session)
            return;
        this.session.pause();
    }

    subscriptions: SubscriptionSet = new SubscriptionSet();

    async checkForMembersUpgrade() {

        // DEBUG: test the upgrade-stream messaging.
        // if (1) {  // NO-COMMIT
        //   this.upgradeStream = this.actionableUpgradeStream = this.stream;
        //   return;
        // }

        let otherStreams: ApiLiveStream[];
        let thisStream = this.stream;
        this.upgradeStream = null;
        this.actionableUpgradeStream = null;
        if (!thisStream || !thisStream.active_live_event || thisStream.premium)
            return;

        try {
            otherStreams = await this.liveStreamsService.active();
        } catch (e) {
            console.error(`Failed to fetch live streams while looking for available members-only upgrades`);
        }

        this.upgradeStream = otherStreams.find(x => x.id != thisStream.id && x.active_live_event && thisStream.active_live_event.id == x.active_live_event.id && x.premium);
        this.actionableUpgradeStream = this.upgradeStream;
    }

    useDarkMode = false;
    upgradeStream: ApiLiveStream;
    actionableUpgradeStream: ApiLiveStream;
    user: ApiUser;

    destroy() {
        this.subscriptions.unsubscribeAll();
    }

    get userEntitled() {
        if (!this.user)
            return false;
        return this.user.entitled;
    }

    overrideFreeStreamPrompt = null;

    get freeStreamPrompt() {
        // if (1)
        //   return 'anonymous';

        if (this.overrideFreeStreamPrompt)
            return this.overrideFreeStreamPrompt;

        if (!this.user)
            return 'anonymous';

        if (!this.userEntitled)
            return 'unentitled';

        return 'entitled';
    }

    get joinUrl() {
        return environment.urls.purchase;
    }

    async init() {
        this.upgradeStream = null;

        let date = new Date();
        this.timezoneString = date.toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
        this.subscriptions.subscribe(this.liveStreamsService.streamChanged, (stream => {
            if (!this.stream || stream.id != this.stream.id)
                return;

            this.stream = stream;
        }));

        this.subscriptions.subscribe(this.userService.userChanged, user => this.user = user);

        this.route.paramMap.subscribe(async params => {
            let streamID = parseInt(params.get('streamID'));
            if (isNaN(streamID)) {
                this.router.navigate([`/`]);
                return;
            }

            this.head.setCanonicalLink(`/live/streams/${streamID}`);

            await this.userService.ready;

            let stream: ApiLiveStream = null;

            try {
                stream = await this.liveStreamsService.get(streamID);
            } catch (e) {
                if (e.json)
                    e = e.json();
                if (e.error == 'stream-ended') {
                    console.error(`Live stream '${streamID}' is not available`);
                } else if (e.error == 'unavailable') {
                    console.error(`Live stream '${streamID}' is not available`);
                } else if (e.error == 'not-found') {
                    console.error(`No such live stream '${streamID}'`);
                } else {
                    console.error(`Uncaught error while fetching live stream:`);
                    console.error(e);
                    debugger;
                }
            }

            if (!stream) {
                this.router.navigate(['/live/schedule']);
                return;
            }

            let title = 'TYT.com';

            if (stream.active_live_event && stream.active_live_event.show) {
                let liveEvent = stream.active_live_event;
                title = liveEvent.show.name;

                if (liveEvent.use_title)
                    title = liveEvent.title;
            }

            this.pageTitle = `[Live] ${title}`;

            this.stream = stream;

            this.checkForMembersUpgrade();

            if (typeof window !== 'undefined') {
                await this.playerReady;
                while (!this.player)
                    await sleep(1000);

                this.player.playStream(this.stream);
            }
        });

        this.audienceType = this.userService.audienceType;
        this.useBantaChat = await this.shell.hasFeature('apps.web.use_banta_chat');
    }

}
